import { GQLContentRichTextFragment, GQLPromoImageFragment, GQLPromoVideoFragment } from '@theprintbar/graphql';
import { cn } from '@theprintbar/shared';
import { useCallback, useEffect, useState } from 'react';
import { Swiper } from 'swiper/types';
import { PrimaryButton } from '../Button';
import { Carousel, CarouselNextButton, CarouselPreviousButton } from '../Carousel';
import { Modal, ModalProps } from '../Modal';
import { RichText } from '../RichText';

interface Step {
	title?: string | null;
	text?: GQLContentRichTextFragment | null;
	image?: GQLPromoImageFragment | null;
	video?: GQLPromoVideoFragment | null;
}

export interface MultiStepModalProps extends ModalProps {
	modalKey: string;
	selectionRequired?: boolean;
	onClose?: () => void;
	steps: {
		structure?: Array<Step | null> | null;
	};
	embedded?: boolean;
}

export function MultiStepModal({ open, onClose, selectionRequired, steps, modalKey, embedded, ...PanelProps }: MultiStepModalProps) {
	const [currentStep, setCurrentStep] = useState(0);
	const [swiper, setSwiper] = useState<Swiper | null>(null);

	useEffect(() => {
		if (!open) setCurrentStep(0);
	}, [open]);

	const stepList = steps.structure || [];

	const handleSlideChange = useCallback(
		(s: Swiper) => {
			console.log('slide change', s.activeIndex);
			console.log('current step', currentStep);
			setCurrentStep(s.activeIndex);
		},
		[currentStep, setCurrentStep],
	);

	const content = (
		<>
			<Carousel
				observer
				observeParents
				parallax
				pagination
				onSwiper={setSwiper}
				onSlideChange={handleSlideChange}
				carouselKey={modalKey}
				slidesPerView={1}
				className="max-w-lg w-full"
				slides={stepList.map((step, index) =>
					step
						? {
								containerProps: {
									className: 'flex flex-col space-y-4 pb-8 px-2',
								},
								content: (
									<>
										<h5 className="uppercase">{step.title}</h5>
										{step.text && <RichText content={step.text.content} className="min-h-16" />}
										{step.video ? (
											<video
												className="w-full aspect-[16/9] object-contain"
												src={step.video.video.url}
												poster={step.video?.preview?.url}
												controls
												controlsList="nodownload"
											/>
										) : step.image ? (
											<img src={step.image.image.url} className="w-full max-h-72 object-contain" />
										) : null}
									</>
								),
						  }
						: null,
				)}
			/>
			<div className="flex mt-4 items-center justify-between">
				<CarouselPreviousButton
					carouselKey={modalKey}
					onClick={() => swiper?.slidePrev()}
					circle={false}
					icon={null}
					className={cn(currentStep === 0 && 'opacity-0')}
				>
					Back
				</CarouselPreviousButton>
				<CarouselNextButton
					carouselKey={modalKey}
					circle={false}
					icon={null}
					onClick={() => swiper?.slideNext()}
					className={cn(currentStep === stepList.length - 1 && 'hidden')}
				>
					Next
				</CarouselNextButton>
				{currentStep === stepList.length - 1 && <PrimaryButton onClick={onClose}>Close</PrimaryButton>}
			</div>
		</>
	);

	return embedded ? (
		content
	) : (
		<Modal {...PanelProps} open={open} onClose={selectionRequired ? undefined : onClose}>
			{content}
		</Modal>
	);
}
